// Notification settings (NOTIFICATIONS.md §4; PLAN-2026-09-26-bell B4): one
// list, grouped as the matrix is, a push and an email switch per row, quiet
// hours, how early a lineup problem is raised, and a mute per league.
//
// EVERYTHING IS IN THE BELL WHATEVER IS SET HERE (§2: in-app is the record),
// and the page says so first, because a switch labeled "off" otherwise reads
// as "I will never hear about this".
//
// Simple removes the sentence under each row, never the row (§4, DETAIL_LEVELS).
// Clean (LOOKS.md Part 7): drawn through the kit.

import { useState } from 'react';
import type { NotificationPrefsShape } from '@fsp/domain';
import { useAtLeast } from '../data/detail';
import { useSession } from '../data/session';
import { useLeagueCards } from '../data/LeaguesProvider';
import { usePrefsScreen, type Channel } from '../data/notificationPrefs';
import { HALF_HOURS } from './shared/clockTimes';
import {
  Button,
  Callout,
  Caption,
  Card,
  Chip,
  Choice,
  Grow,
  Line,
  Segments,
  Stack,
  Text,
  Title,
  Toggle,
  tell,
} from '../kit/ui';

const LEAD: readonly { id: NotificationPrefsShape['lineupLeadMinutes']; label: string }[] = [
  { id: '30', label: '30 min' },
  { id: '60', label: '1 h' },
  { id: '120', label: '2 h' },
  { id: '240', label: '4 h' },
  { id: '720', label: '12 h' },
];

/** Every half hour, as a person picks a bedtime. */
const TIMES = HALF_HOURS;

export function NotificationSettingsScreen() {
  const screen = usePrefsScreen();
  const { cards } = useLeagueCards();
  const explain = useAtLeast('standard');
  const said = (problem: string | null) => {
    if (problem) tell(problem, 'bad');
  };

  if (screen.state !== 'ready' || screen.matrix === null || screen.prefs === null) {
    return (
      <div className="fsp-screen">
        <div className="fsp-prefs">
          {screen.state === 'failed' ? (
            <Callout tone="bad">{screen.problem}</Callout>
          ) : (
            <Text tone="muted">Reading your notification settings.</Text>
          )}
        </div>
      </div>
    );
  }
  const prefs = screen.prefs;
  const quiet = prefs.quietHours;

  return (
    <div className="fsp-screen">
      <div className="fsp-prefs">
        <Stack gap={4}>
          <Stack gap={1}>
            <Title>Notifications</Title>
            <Text tone="secondary">
              Everything is always in the bell. These decide what also reaches your devices and your
              email.
            </Text>
            {explain ? (
              <Text tone="muted" size="xs">
                What you can’t miss is always sent: to every device with notifications on, through
                quiet hours and a muted league, and by email when there is no device to push to.
              </Text>
            ) : null}
          </Stack>
          <YourEmail />

          {screen.matrix.groups.map((g) => (
            <Card key={g.id} padded>
              <Stack gap={2}>
                <Line spread>
                  <Title>{g.title}</Title>
                  <Line gap={4}>
                    <Caption>Push</Caption>
                    <Caption>Email</Caption>
                  </Line>
                </Line>
                {g.rows.map((r) => (
                  <Line key={r.type} spread>
                    <Grow>
                      <Stack gap={0}>
                        <Line gap={2} wrap>
                          <Text>{r.label}</Text>
                          {r.lockCritical ? <Chip tone="accent">Can’t miss</Chip> : null}
                        </Line>
                      </Stack>
                    </Grow>
                    <Line gap={4}>
                      {/* A ROW YOU CAN'T MISS IS ALWAYS SENT (NOTIFICATIONS.md §2): push
                          to every device, email only when there is none, whatever a
                          switch says. So its switches show that, and do not move. */}
                      {(['push', 'email'] as Channel[]).map((c) => (
                        <Toggle
                          key={c}
                          hideLabel
                          label={
                            r.lockCritical
                              ? `${r.label}: ${c === 'push' ? 'push, always' : 'email when there is no device, always'}`
                              : `${r.label}: ${c}`
                          }
                          on={r.lockCritical ? true : screen.channelOn(r.type, c)}
                          disabled={r.lockCritical}
                          onChange={(on) => void screen.setChannel(r.type, c, on).then(said)}
                        />
                      ))}
                    </Line>
                  </Line>
                ))}
              </Stack>
            </Card>
          ))}

          <Card padded>
            <Stack gap={2}>
              <Line spread>
                <Title>Quiet hours</Title>
                <Toggle
                  hideLabel
                  label="Quiet hours"
                  on={quiet !== null}
                  onChange={(on) =>
                    void screen
                      .change((p) => ({
                        ...p,
                        quietHours: on ? { from: '22:00', to: '08:00' } : null,
                      }))
                      .then(said)
                  }
                />
              </Line>
              <Text tone="secondary">
                What you can’t miss comes through anyway — a draft about to start, your pick, a
                lineup that cannot score. Missing a draft is worse than being woken.
              </Text>
              {quiet !== null ? (
                <Line gap={3} wrap>
                  <Choice
                    label="From"
                    options={TIMES}
                    value={quiet.from}
                    empty="No such time."
                    onChange={(from) =>
                      from &&
                      void screen
                        .change((p) => ({ ...p, quietHours: { from, to: quiet.to } }))
                        .then(said)
                    }
                  />
                  <Choice
                    label="Until"
                    options={TIMES}
                    value={quiet.to}
                    empty="No such time."
                    onChange={(to) =>
                      to &&
                      void screen
                        .change((p) => ({ ...p, quietHours: { from: quiet.from, to } }))
                        .then(said)
                    }
                  />
                </Line>
              ) : null}
              {prefs.timeZone ? (
                explain ? (
                  <Text tone="muted" size="xs">
                    In {prefs.timeZone}, the zone on your account.
                  </Text>
                ) : null
              ) : (
                <Text tone="warn" size="xs">
                  Your account has no time zone yet, so quiet hours wait until it has one. Set it on
                  your Account page.
                </Text>
              )}
            </Stack>
          </Card>

          <Card padded>
            <Stack gap={2}>
              <Title>A lineup that cannot score</Title>
              <Text tone="secondary">How long before the first lock of the week to say so.</Text>
              <Segments
                label="How early"
                items={LEAD}
                value={prefs.lineupLeadMinutes}
                onChange={(lead) =>
                  void screen.change((p) => ({ ...p, lineupLeadMinutes: lead })).then(said)
                }
              />
            </Stack>
          </Card>

          {cards.length > 0 ? (
            <Card padded>
              <Stack gap={2}>
                <Title>Your leagues</Title>
                {explain ? (
                  <Text tone="secondary">
                    A muted league sends only what you can’t miss. The rest is still in the bell.
                  </Text>
                ) : null}
                {cards.map((c) => (
                  <Line key={c.league.id} spread>
                    <Text>{c.league.name}</Text>
                    <Toggle
                      label="Muted"
                      on={prefs.leagues[c.league.id]?.muted === true}
                      onChange={(muted) =>
                        void screen
                          .change((p) => {
                            const leagues = { ...p.leagues };
                            const rest = { ...(leagues[c.league.id] ?? {}) };
                            if (muted) rest.muted = true;
                            else delete rest.muted;
                            if (Object.keys(rest).length === 0) delete leagues[c.league.id];
                            else leagues[c.league.id] = rest;
                            return { ...p, leagues };
                          })
                          .then(said)
                      }
                    />
                  </Line>
                ))}
              </Stack>
            </Card>
          ) : null}
        </Stack>
      </div>
    </div>
  );
}

/**
 * WHERE EMAIL GOES (PLAN-2026-10-18-families-and-newcomers §7.1): the
 * account's address, and while it is unconfirmed, the link that confirms it.
 * Nothing but account mail reaches an address its owner has not confirmed, so
 * the email switches below say what WILL be sent once it is.
 */
function YourEmail() {
  const { session, askConfirmLink } = useSession();
  const [stage, setStage] = useState<'idle' | 'sending' | 'sent'>('idle');
  if (session === null || session.email === '') return null;
  if (session.emailConfirmed === true) {
    return (
      <Text tone="secondary" size="sm">
        Email goes to {session.email}.
      </Text>
    );
  }
  const ask = async () => {
    if (stage === 'sending') return;
    setStage('sending');
    const outcome = await askConfirmLink();
    if (outcome.ok) {
      setStage('sent');
      return;
    }
    setStage('idle');
    tell(outcome.message, outcome.code === 'conflict' ? 'neutral' : 'bad');
  };
  return (
    <Callout tone="neutral">
      <Stack gap={2}>
        <Text>
          {stage === 'sent'
            ? `A link is on its way to ${session.email}. Open it to confirm the address; it works for seven days.`
            : `Email goes to ${session.email} once you confirm it’s yours. Until then nothing but account mail, like a password reset, is sent there.`}
        </Text>
        {stage === 'sent' ? null : (
          <Line>
            <Button variant="secondary" onClick={() => void ask()} disabled={stage === 'sending'}>
              {stage === 'sending' ? 'Sending…' : 'Email me the link'}
            </Button>
          </Line>
        )}
      </Stack>
    </Callout>
  );
}
